"use client"

import * as React from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { Loader } from "@/components/ai-elements/loader"
import { ProgressBarCard } from "@/components/ui/progress-bar-card"
import { CircularProgress } from "@/components/ui/circular-progress"
import { useInsightsWeek } from "@/context/insights-week-context"
import { format, addDays } from "date-fns"
import { TrendingUp, TrendingDown } from "@/components/icons"
import { cn } from "@/lib/utils"

// Simple animated number component
function AnimatedNumber({ value, suffix = "%" }: { value: number; suffix?: string }) {
  const [displayValue, setDisplayValue] = React.useState(0)

  React.useEffect(() => {
    const duration = 2000 // 2 seconds
    const startTime = Date.now()
    const startValue = 0

    const animate = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4)
      const currentValue = Math.round(startValue + (value - startValue) * easeOutQuart)
      
      setDisplayValue(currentValue)
      
      if (progress < 1) {
        requestAnimationFrame(animate)
      }
    }

    requestAnimationFrame(animate)
  }, [value])

  return (
    <span className="text-5xl md:text-6xl font-bold tabular-nums text-primary">
      {displayValue}<span className="text-3xl md:text-4xl">{suffix}</span>
    </span>
  )
}

type ExecutionViewBy = 'life_domain' | 'label' | 'priority' | 'type'

interface ExecutionItem {
  label: string
  actual: number
  planned: number
  completion: number
  color: string
  useSecondaryBackground?: boolean
}

function getExecutionItems(
  data: NonNullable<NonNullable<IntentionalLivingDisciplineCardProps['data']>['planned_data']>['data'],
  viewBy: ExecutionViewBy,
): ExecutionItem[] {
  if (!data) return []

  switch (viewBy) {
    case 'life_domain':
      return (data.life_domain ?? [])
        .sort((a, b) => (a.sort_order ?? Infinity) - (b.sort_order ?? Infinity))
        .map(d => ({ label: d.label, actual: d.actual, planned: d.planned, completion: d.completion, color: d.color }))
    case 'label':
      return (data.label ?? []).map(l => ({
        label: l.label, actual: l.actual, planned: l.planned, completion: l.completion, color: l.color,
        useSecondaryBackground: true,
      }))
    case 'priority':
      return (data.priority ?? []).map(p => ({
        label: p.label, actual: p.actual, planned: p.planned, completion: p.completion, color: p.color,
        useSecondaryBackground: true,
      }))
    case 'type':
      return (data.type ?? []).map(t => ({
        label: t.label, actual: t.actual, planned: t.planned, completion: t.completion, color: t.color,
        useSecondaryBackground: true,
      }))
  }
}

function ExecutionDetails({ data }: {
  data: NonNullable<NonNullable<IntentionalLivingDisciplineCardProps['data']>['planned_data']>['data']
}) {
  const [viewBy, setViewBy] = React.useState<ExecutionViewBy>('life_domain')
  const items = getExecutionItems(data, viewBy)

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 justify-end">
        <span className="text-sm text-muted-foreground whitespace-nowrap">View by</span>
        <Select value={viewBy} onValueChange={(v) => setViewBy(v as ExecutionViewBy)}>
          <SelectTrigger className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="life_domain">Life domain</SelectItem>
            <SelectItem value="label">Labels</SelectItem>
            <SelectItem value="priority">Priority</SelectItem>
            <SelectItem value="type">Type</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {items.length > 0 ? (
        <div className="space-y-3">
          {items.map((item, index) => (
            <ProgressBarCard
              key={`${viewBy}-${index}`}
              label={item.label}
              actual={item.actual}
              goal={item.planned}
              completion={item.completion}
              color={item.color}
              animate={true}
              delay={index * 100}
              useSecondaryBackground={item.useSecondaryBackground}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-4">
          <p className="text-muted-foreground text-sm">No data available</p>
        </div>
      )}
    </div>
  )
}

interface IntentionalLivingDisciplineCardProps {
  data?: {
    planned_data?: {
      hasPlan: boolean
      data?: {
        aggregate: {
          intentional_living_actual: number
          intentional_living_planned: number
          intentional_living_completion: number
          intentional_living_commitment: number
          wow_execution?: number | null
        }
        daily?: Array<{
          day: string
          actual: number
          planned: number
          completion: number
        }>
        life_domain?: Array<{
          label: string
          life_domain: string
          color: string
          is_intentional_living: boolean
          sort_order?: number
          actual: number
          planned: number
          completion: number
        }>
        label?: Array<{
          label: string
          label_id: string
          color: string
          is_intentional_living: boolean
          actual: number
          planned: number
          completion: number
        }>
        priority?: Array<{
          label: string
          priority: string
          color: string
          is_intentional_living: boolean
          actual: number
          planned: number
          completion: number
        }>
        type?: Array<{
          label: string
          type: string
          color: string
          is_intentional_living: boolean
          actual: number
          planned: number
          completion: number
        }>
      }
    }
  } | null
  loading?: boolean
  className?: string
}

export function IntentionalLivingDisciplineCard({ data, loading, className }: IntentionalLivingDisciplineCardProps) {
  const { currentWeekStart } = useInsightsWeek()

  // Generate 7 days starting from currentWeekStart
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const day = addDays(currentWeekStart, i)
    return {
      date: day,
      dayName: format(day, 'EEE'), // Mon, Tue, Wed, etc.
      dateString: format(day, 'yyyy-MM-dd')
    }
  })

  console.log('🗓️ Generated weekDays:', weekDays.map(d => `${d.dayName} ${d.dateString}`))
  console.log('📊 API daily data:', data?.planned_data?.data?.daily)

  if (loading) {
    return (
      <Card className={cn(className)}>
        <CardHeader>
          <CardTitle>Intentional living discipline</CardTitle>
        </CardHeader>
        <CardContent data-slot="card-content" className="px-6">
          <div className="flex flex-col items-center justify-center py-8 space-y-3">
            <Loader size={24} className="text-muted-foreground" />
            <div className="text-muted-foreground text-sm">Loading results...</div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={cn(className)}>
      <CardHeader>
        <CardTitle>Intentional living discipline</CardTitle>
      </CardHeader>
      <CardContent data-slot="card-content" className="px-6">
        <div className="space-y-6">
          {data?.planned_data?.hasPlan === false ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No planned data available</p>
            </div>
          ) : data?.planned_data?.hasPlan === true && data?.planned_data?.data?.aggregate ? (
            <div className="space-y-6">
              {/* Intentional Living Section */}
              <div className="flex gap-8 items-start justify-center">
                {/* Big number on the left */}
                <div className="flex flex-col gap-3 flex-shrink-0">
                  <div>
                    <div>
                      {data.planned_data.data.aggregate.intentional_living_completion !== undefined && 
                       data.planned_data.data.aggregate.intentional_living_completion !== null ? (
                        <AnimatedNumber value={data.planned_data.data.aggregate.intentional_living_completion} />
                      ) : (
                        <span className="text-6xl font-bold text-primary">-</span>
                      )}
                    </div>
                  </div>
                  {data.planned_data.data.aggregate.wow_execution !== null && data.planned_data.data.aggregate.wow_execution !== undefined && (
                    <Badge 
                      variant="secondary" 
                      className={`w-fit ${
                        data.planned_data.data.aggregate.wow_execution > 0 
                          ? 'text-green-600 hover:bg-green-50' 
                          : data.planned_data.data.aggregate.wow_execution < 0 
                            ? 'text-red-600 hover:bg-red-50' 
                            : 'text-black hover:bg-gray-50'
                      }`}
                    >
                      {data.planned_data.data.aggregate.wow_execution !== 0 && (
                        data.planned_data.data.aggregate.wow_execution > 0 ? (
                          <TrendingUp className="h-3 w-3 mr-1" />
                        ) : (
                          <TrendingDown className="h-3 w-3 mr-1" />
                        )
                      )}
                      {data.planned_data.data.aggregate.wow_execution}%
                    </Badge>
                  )}
                </div>

                {/* Metrics on the right */}
                <div className="flex-1 space-y-2">
                  {/* Row 1: Executed hours */}
                  <div className="text-sm">
                    <span className="font-bold text-card-foreground">
                      {data.planned_data.data.aggregate.intentional_living_actual ?? "-"}h
                    </span>
                    <span className="text-card-foreground/70"> executed</span>
                  </div>

                  {/* Row 2: Planned hours */}
                  <div className="text-sm">
                    <span className="font-bold text-card-foreground">
                      {data.planned_data.data.aggregate.intentional_living_planned ?? "-"}h
                    </span>
                    <span className="text-card-foreground/70"> planned</span>
                  </div>

                  {/* Row 3: Commitment percentage */}
                  <div className="text-sm">
                    <span className="font-bold text-card-foreground">
                      {data.planned_data.data.aggregate.intentional_living_commitment !== undefined && 
                       data.planned_data.data.aggregate.intentional_living_commitment !== null
                        ? `${Math.round(data.planned_data.data.aggregate.intentional_living_commitment)}%`
                        : "-"}
                    </span>
                    <span className="text-card-foreground/70"> committed</span>
                  </div>
                </div>
              </div>

              {/* Daily Results Section */}
              {data?.planned_data?.data?.daily && data.planned_data.data.daily.length > 0 && (
                <div>
                  <div className="grid grid-cols-7 gap-2">
                    {weekDays.map(({ dayName, dateString }, index) => {
                      // Find matching data from API with proper null checking
                      const dayData = data?.planned_data?.data?.daily?.find(
                        item => item.day === dateString
                      )
                      
                      return (
                        <div key={dateString} className="flex flex-col items-center space-y-1">
                          <div className="text-xs font-medium text-muted-foreground">
                            {dayName}
                          </div>
                          {dayData ? (
                            <>
                              <div className="text-xs font-medium text-foreground">
                                {dayData.completion}%
                              </div>
                              <CircularProgress 
                                value={dayData.completion} 
                                size={32}
                                strokeWidth={4}
                                animate={true}
                                delay={index * 100}
                              />
                            </>
                          ) : (
                            <div className="w-8 h-8 flex items-center justify-center">
                              <span className="text-xs text-muted-foreground">-</span>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Execution Details Accordion */}
              {(data.planned_data.data.life_domain && data.planned_data.data.life_domain.length > 0) ||
               (data.planned_data.data.label && data.planned_data.data.label.length > 0) ||
               (data.planned_data.data.priority && data.planned_data.data.priority.length > 0) ||
               (data.planned_data.data.type && data.planned_data.data.type.length > 0) ? (
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="more-details" className="border-none">
                    <AccordionTrigger>Execution details</AccordionTrigger>
                    <AccordionContent>
                      <ExecutionDetails data={data.planned_data.data} />
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              ) : null}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No planned data available</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

